// Form folders — the folder buckets the Forms-templates list is organised into.
// GET /form-folders lists them; POST/PATCH/DELETE are the Manage-folders modal's
// create / rename / remove. The list response is confirmed as a bare array of
// `{ _id, title, linkedIds }`, so the raw type states the contract strictly.
// A folder carries only a title — the API's optional color / isHidden /
// isLocked fields are deliberately not modelled (no UI, and nothing sends them).

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** A form folder. `formIds` is the raw `linkedIds` — the forms filed in it; its
 *  length is the count the folders panel and the modal rows show. */
export interface FormFolder {
  id: string;
  title: string;
  formIds: string[];
}

/** POST /form-folders + PATCH /form-folders/:id body. */
export interface FormFolderPayload {
  title: string;
}

// ---- raw shapes (per the confirmed list response) ----
interface ApiFormFolder {
  _id: string;
  title: string;
  linkedIds: string[];
}

const toFormFolder = (f: ApiFormFolder): FormFolder => ({
  id: f._id,
  title: f.title,
  formIds: f.linkedIds,
});

export const formFoldersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFormFolders: build.query<FormFolder[], void>({
      query: () => ({ ...ROUTES.formFolders.list }),
      transformResponse: (res: ApiFormFolder[]) => res.map(toFormFolder),
      providesTags: [{ type: 'FormFolder', id: 'LIST' }],
    }),
    createFormFolder: build.mutation<unknown, FormFolderPayload>({
      query: (body) => ({ ...ROUTES.formFolders.create, data: body }),
      invalidatesTags: [{ type: 'FormFolder', id: 'LIST' }],
    }),
    updateFormFolder: build.mutation<unknown, { id: string; body: FormFolderPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.formFolders.update(id), data: body }),
      invalidatesTags: [{ type: 'FormFolder', id: 'LIST' }],
    }),
    deleteFormFolder: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.formFolders.remove(id) }),
      invalidatesTags: [{ type: 'FormFolder', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetFormFoldersQuery,
  useCreateFormFolderMutation,
  useUpdateFormFolderMutation,
  useDeleteFormFolderMutation,
} = formFoldersApi;
